<template>
  <div class="d-flex ga-2 align-center" data-id-e2e="btnImport">
    <v-btn
      variant="flat"
      primary
      :disabled="!hasSelectedAssortmentGroups || !canEditWorkpackageScope || shouldBeDisabled"
      :loading="isImportingFromPlano"
      @click="importFromPlanogram()"
    >
      {{ $t('actions.importAssortmentGroups') }}
      <v-icon end color="background"> mdi-download </v-icon>
    </v-btn>
    <AssortmentTooltip
      :title="$t('tooltips.importFromAssortmentGroups.title')"
      :tooltip-sections="importFromAssortmentGroupsToolTipSections"
    />
    <DependencyTreeFeedbackModal
      :value="dependencyTreeModalOpen"
      :results="dependencyTreeFeedback"
      page="workpackageSetup"
      @close="closeDependencyTreeModal"
      @commit="importFromPlanogram(true)"
    />
  </div>
</template>

<script>
import { map, get, keyBy, filter, omit, uniqBy, reduce, concat, keys, size } from 'lodash';
import { mapState, mapActions } from 'pinia';
import originSourceTypes from '@enums/origin-source-types';
import assortmentGroupTypes from '@enums/assortment-group-types';
import { useWorkpackageScopeTooltipOptions } from '@/composables/use-tooltip-options';
// Stores
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useContextStore } from '@/stores/context';
import { useWorkpackageProductsStore } from '@/stores/workpackage-products';
import { useDependencyTreeStore } from '@/stores/dependency-tree';
import { useToolDataStore } from '@/stores/tool-data';
import { useSnackbarStore } from '@/stores/snackbar';
import { useScenariosStore } from '@/stores/scenarios';
import { useFurnitureStore } from '@/stores/furniture';
import { useScenarioProductsStore } from '@/stores/scenario-products';
// composables
import { useDayjs } from '@/composables/use-dayjs';
import { usePermission } from '@/composables/use-permission';
import { useFeature } from '@/composables/use-feature';

export default {
  props: {
    shouldBeDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  setup() {
    const { canEditWorkpackageScope } = usePermission();
    const { importFromAssortmentGroupsToolTipSections } = useWorkpackageScopeTooltipOptions();
    const { formatDateForPicker, getMinDate, getMaxDate, dayjs } = useDayjs();
    const { hasProductsExistingFromNewInTemplateEnabled } = useFeature();
    return {
      canEditWorkpackageScope,
      importFromAssortmentGroupsToolTipSections,
      formatDateForPicker,
      getMinDate,
      getMaxDate,
      dayjs,
      hasProductsExistingFromNewInTemplateEnabled,
    };
  },
  data() {
    return {
      dependencyTreeFeedback: {},
      dependencyTreeModalOpen: false,
    };
  },

  computed: {
    ...mapState(useWorkpackagesStore, [
      'selectedWorkpackage',
      'isImportingFromPlano',
      'wpSelectedAssortmentGroups',
    ]),
    ...mapState(useContextStore, ['clientConfig', 'getDefaultReverseFormat']),
    ...mapState(useWorkpackageProductsStore, ['workpackageProducts']),

    workpackage() {
      return this.selectedWorkpackage;
    },

    hasSelectedAssortmentGroups() {
      return this.wpSelectedAssortmentGroups.length > 0;
    },

    isSplitWorkpackagesEnabled() {
      return get(this.clientConfig, 'features.splitWorkpackagesEnabled');
    },

    isMaxRangePerformanceLearningSwitchingPeriodEnabled() {
      return get(this.clientConfig, 'features.maxRangePerformanceLearningSwitchingPeriod');
    },

    selectedAssortmentGroupKeys() {
      return (
        (this.hasSelectedAssortmentGroups && this.wpSelectedAssortmentGroups.map(ag => ag.key)) ||
        []
      );
    },

    assortmentGroupKeysSelectedForAssortment() {
      return (
        (this.hasSelectedAssortmentGroups &&
          this.wpSelectedAssortmentGroups.filter(ag => ag.assortment).map(ag => ag.key)) ||
        []
      );
    },

    assortmentGroupKeysSelectedForAnalysis() {
      return (
        (this.hasSelectedAssortmentGroups &&
          this.wpSelectedAssortmentGroups.filter(ag => ag.analysis).map(ag => ag.key)) ||
        []
      );
    },

    useNewVersionOfAGSelection() {
      return get(this.clientConfig, 'features.useNewVersionOfAGSelection');
    },

    importFurnitureEnabled() {
      // Only import furniture for furniture assortment groups
      const assortmentGroupType = get(
        this.clientConfig,
        'assortmentGroups.mainType',
        assortmentGroupTypes.furniture
      );
      return (
        assortmentGroupType === assortmentGroupTypes.furniture &&
        get(this.clientConfig, 'features.importPlanogramFurnitureEnabled', false)
      );
    },
  },

  methods: {
    ...mapActions(useDependencyTreeStore, ['triggerDependencyTree']),
    ...mapActions(useFurnitureStore, ['runWorkpackageFurnitureSetup', 'fetchWorkpackageFurniture']),
    ...mapActions(useToolDataStore, [
      'getStoreDetails',
      'getProducts',
      'getProductsByAssortmentGroup',
    ]),
    ...mapActions(useWorkpackagesStore, [
      'updateWorkpackage',
      'fetchSingleWorkpackage',
      'setIsImportingFromPlano',
      'setLastSuccessfulImportFromPlanoTimestamp',
      'getWpDataForSave',
    ]),
    ...mapActions(useWorkpackageProductsStore, [
      'createWorkpackageProducts',
      'deleteWorkpackageProducts',
      'fetchWorkpackageProducts',
    ]),
    ...mapActions(useSnackbarStore, ['showSnackbar', 'showSuccess', 'showError']),
    ...mapActions(useScenariosStore, ['fetchScenarios']),
    ...mapActions(useScenarioProductsStore, ['fetchScenarioProducts']),

    async importFromPlanogram(commit = false) {
      const results = await this.triggerDependencyTree({
        params: {
          change: 'workpackageSetupModified',
          updates: { global: { selectedAssortmentGroupKeys: this.selectedAssortmentGroupKeys } },
          commit,
          workpackageId: this.workpackage._id,
        },
      });

      if (results.needsFeedback) {
        this.dependencyTreeFeedback = results.output;
        this.dependencyTreeModalOpen = true;
        return;
      }

      this.setIsImportingFromPlano(true);

      try {
        // get stores & products from tool-data collections
        const storesFilter = { selectedAssortmentGroupKeys: this.selectedAssortmentGroupKeys };

        const startDate = this.isMaxRangePerformanceLearningSwitchingPeriodEnabled
          ? this.getMinDate(
              [
                this.dayjs.utc(this.workpackage.performanceStartDate),
                this.dayjs.utc(this.workpackage.learningStartDate),
              ].filter(d => d.isValid())
            )
          : this.workpackage.switchingStartDate;

        const endDate = this.isMaxRangePerformanceLearningSwitchingPeriodEnabled
          ? this.getMaxDate(
              [
                this.dayjs.utc(this.workpackage.performanceEndDate),
                this.dayjs.utc(this.workpackage.learningEndDate),
              ].filter(d => d.isValid())
            )
          : this.workpackage.switchingEndDate;

        const productsFilter = {
          selectedAssortmentGroupKeys: this.selectedAssortmentGroupKeys,
          switchingStartDate: this.formatDateForPicker(startDate, this.getDefaultReverseFormat),
          switchingEndDate: this.formatDateForPicker(endDate, this.getDefaultReverseFormat),
        };

        const importedStores = await this.getStoreDetails(storesFilter);
        // based on selected assortment groups create list of store keys that should be included in for analysis or/and for assortment selection
        const storeKeysSelectedForAssortment = new Set(
          map(
            importedStores.filter(s =>
              this.assortmentGroupKeysSelectedForAssortment.some(r =>
                s.associatedAssortmentGroups.includes(r)
              )
            ),
            'storeKey'
          )
        );
        const storeKeysSelectedForAnalysis = new Set(
          map(
            importedStores.filter(s =>
              this.assortmentGroupKeysSelectedForAnalysis.some(r =>
                s.associatedAssortmentGroups.includes(r)
              )
            ),
            'storeKey'
          )
        );

        // Keep the manual stores which are not being pulled in from AG
        const allStoreKeys = new Set(map(importedStores, 'storeKey'));
        const importedStoresForUpdate = uniqBy(
          importedStores.map(s => omit(s, 'associatedAssortmentGroups')),
          'storeKey'
        );

        const originalStores = filter(
          this.workpackage.stores,
          ({ originSource, storeKey }) =>
            originSource === originSourceTypes.existingManually && !allStoreKeys.has(storeKey)
        );
        const updatedStores = [
          ...importedStoresForUpdate,
          ...(this.isImportingFromPlano ? [] : originalStores),
        ];

        // Keep the manual products which are not being pulled in from AG
        const importedProducts = await this.getProductsByAssortmentGroup(productsFilter);
        const importedProductKeys = new Set(map(importedProducts, 'productKey'));

        const updatedProducts = [
          ...filter(
            this.workpackageProducts,
            ({ originSource, productKey }) =>
              originSource === originSourceTypes.existingManually &&
              !importedProductKeys.has(productKey)
          ),
          ...importedProducts,
        ];

        let productsFromTemplate = {};
        let storesFromTemplate = {};
        let semiNewProductsFromTemplate = [];

        // Get template data
        if (this.workpackage.templateId) {
          // Fetch all workpackage products from the template
          const templateWorkpackageProducts = await this.fetchWorkpackageProducts({
            workpackageId: this.workpackage.templateId,
            options: { isStatelessFetch: true },
          });
          const template = await this.fetchSingleWorkpackage({
            workpackageId: this.workpackage.templateId,
            includeStores: true,
            isStatelessFetch: true,
          });
          productsFromTemplate = keyBy(templateWorkpackageProducts, 'productKey');
          storesFromTemplate = keyBy(template.stores, 'storeKey');

          if (this.hasProductsExistingFromNewInTemplateEnabled) {
            const newProductsFromTemplate = await this.getNewProductsFromTemplate();
            // Filter out new products that have already been imported
            const existingProductKeyDisplays = new Set(map(updatedProducts, 'productKeyDisplay'));
            const updatedNewProducts = filter(
              newProductsFromTemplate,
              p => !existingProductKeyDisplays.has(p.productKeyDisplay)
            );
            const updatedNewProductsByKey = keyBy(updatedNewProducts, 'productKeyDisplay');
            // Get data for semi-new products from tooldata. They should be treated as existing products
            if (size(updatedNewProductsByKey)) {
              const semiNewProducts = await this.getProducts({
                params: { productKeyDisplays: keys(updatedNewProductsByKey) },
              });
              semiNewProductsFromTemplate = map(semiNewProducts, p => {
                const { fromTemplate, originSource } = updatedNewProductsByKey[p.productKeyDisplay];
                return { ...p, fromTemplate, originSource };
              });
            }
          }
        }

        // we should add stores to the workpackage setup by setting analysis: true, assortment: true
        // and replacing storeProfile with simpleSwapsCluster
        const workpackageStores = map(updatedStores, s => ({
          ...Object.assign(s, {
            analysis: this.useNewVersionOfAGSelection
              ? storeKeysSelectedForAnalysis.has(s.storeKey)
              : get(storesFromTemplate, [s.storeKey, 'analysis'], true),
            assortment: this.useNewVersionOfAGSelection
              ? storeKeysSelectedForAssortment.has(s.storeKey)
              : get(storesFromTemplate, [s.storeKey, 'assortment'], true),
            simpleSwapsCluster: s.storeProfile,
          }),
        }));

        // Track that products were added to workpackage/template from AGs
        const newWorkpackageProducts = reduce(
          updatedProducts,
          (acc, p) => {
            const isFromTemplateIntoNonTemplate =
              !!productsFromTemplate[p.productKey] && !this.workpackage.isTemplate;

            const itemToPush = {
              ...p,
              fromTemplate: !!productsFromTemplate[p.productKey] || !!this.workpackage.isTemplate,
              originSource: p.originSource || originSourceTypes.existingFromAG,
            };

            // If a product comes from template into non-template WP, only leave it if hasn't been excluded
            if (isFromTemplateIntoNonTemplate) {
              const isIncludedInTemplate = productsFromTemplate[p.productKey].included;
              if (isIncludedInTemplate) {
                itemToPush.included = true;
                acc.push(itemToPush);
              }
            } else if (this.workpackage.isTemplate) {
              // If a product comes into template, always include it
              itemToPush.included = true;
              acc.push(itemToPush);
            } else {
              acc.push(itemToPush);
            }

            return acc;
          },
          []
        );
        // If feature enabled, semi-new products should be treated as existing products and
        // will be added to workpackage products
        const workpackageProductsUpdates = concat(
          newWorkpackageProducts,
          semiNewProductsFromTemplate
        );
        await Promise.all([
          await this.updateWorkpackageStores(workpackageStores),
          await this.updateWorkpackageProducts(workpackageProductsUpdates),
          // Workpackage scope data must be saved before furniture data can be generated
          ...(this.importFurnitureEnabled
            ? [
                this.updateWorkpackage({
                  workpackageId: this.workpackage._id,
                  updates: this.getWpDataForSave(),
                }),
              ]
            : []),
        ]);
        // Generate furniture data
        if (this.importFurnitureEnabled) await this.runWorkpackageFurnitureSetup();

        // fetch furniture for split filter
        if (this.isSplitWorkpackagesEnabled && !this.workpackage.isTemplate) {
          await this.fetchWorkpackageFurniture();

          // Trigger scope filters to apply CFC filtering if needed
          this.setLastSuccessfulImportFromPlanoTimestamp(Date.now());
        }

        this.showSuccess(this.$t('notifications.planograms.finished'));
      } catch (e) {
        this.showError(this.$t('notifications.planograms.failed'));
        console.error(e);
      } finally {
        this.setIsImportingFromPlano(false);
      }
    },

    async getNewProductsFromTemplate() {
      // New products are stored at scenario level. In order to retrieve the new
      // products from the template, we must first find the template scenarioId
      const templateScenarios = await this.fetchScenarios({
        params: {
          where: { workpackageId: this.workpackage.templateId },
          pick: ['_id'],
        },
        options: { skipCommit: true },
      });
      const templateScenarioId = get(templateScenarios[0], '_id');
      // Fetch new products from the template scenario
      const templateNewProducts = await this.fetchScenarioProducts({
        params: { scenarioId: templateScenarioId, where: { isNewProduct: true } },
        options: { shouldCommit: false },
      });
      return templateNewProducts;
    },

    async updateWorkpackageStores(stores) {
      await this.updateWorkpackage({
        workpackageId: this.workpackage._id,
        updates: {
          stores,
          selectedAssortmentGroupSettings: this.selectedAssortmentGroupKeys.map(key => ({
            key,
            analysis: true,
            assortment: true,
          })),
        },
      });
    },

    async updateWorkpackageProducts(products) {
      // Delete all the workpackage products so we can be sure
      // that we are not adding duplicated products
      await this.deleteWorkpackageProducts({ workpackageId: this.workpackage._id });
      await this.createWorkpackageProducts({
        workpackageId: this.workpackage._id,
        products,
      });
    },

    closeDependencyTreeModal() {
      this.dependencyTreeModalOpen = false;
      this.dependencyTreeFeedback = {};
    },
  },
};
</script>

<style scoped></style>
